import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList, Plus, X, AlertCircle, Repeat, CalendarClock, Pencil, Trash2, Pause, Play,
} from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import WhsGate from '../../../components/whs/WhsGate';
import Button from '../../../components/ui/Button';
import whsService from '../../../services/whs';
import userService from '../../../services/user';
import type { UserData } from '../../../services/user';
import type {
  InspectionListItem, InspectionStatus, ChecklistTemplate, FarmOption,
  InspectionSchedule, ScheduleFrequency,
} from '../../../types/whs';
import { Badge, INSPECTION_STATUS_LABEL, INSPECTION_STATUS_CLASS } from '../../../components/whs/whsConstants';
import { useOrgTimezone, formatLocalDateInTz } from '../../../utils/timezone';

const STATUS_FILTERS: Array<{ value: '' | InspectionStatus; label: string }> = [
  { value: '', label: 'All' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const FREQUENCY_LABEL: Record<ScheduleFrequency, string> = {
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  biannual: 'Every 6 months',
  annual: 'Annual',
  custom: 'Custom',
};

function userName(u: UserData): string {
  const full = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return full || u.username;
}

function inspectorName(i: { inspector_first_name?: string | null; inspector_last_name?: string | null }): string {
  const full = `${i.inspector_first_name || ''} ${i.inspector_last_name || ''}`.trim();
  return full || '—';
}

interface ScheduleForm {
  title: string;
  frequency: ScheduleFrequency;
  interval_days: string;
  start_date: string;
  template_id: string;
  area: string;
  farm_id: string;
  inspector_id: string;
  notes: string;
}

const EMPTY_SCHEDULE: ScheduleForm = {
  title: '', frequency: 'monthly', interval_days: '', start_date: '',
  template_id: '', area: '', farm_id: '', inspector_id: '', notes: '',
};

function WhsInspectionsInner() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();

  const [inspections, setInspections] = useState<InspectionListItem[]>([]);
  const [schedules, setSchedules] = useState<InspectionSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'' | InspectionStatus>('');

  // Shared option lists (templates / farms / users) for both modals.
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [farms, setFarms] = useState<FarmOption[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);

  // One-off inspection modal.
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [area, setArea] = useState('');
  const [farmId, setFarmId] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [inspectorId, setInspectorId] = useState('');

  // Schedule modal.
  const [showSchedule, setShowSchedule] = useState(false);
  const [editingScheduleId, setEditingScheduleId] = useState<number | null>(null);
  const [scheduleForm, setScheduleForm] = useState<ScheduleForm>(EMPTY_SCHEDULE);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [insRes, schRes] = await Promise.all([
      whsService.listInspections(statusFilter ? { status: statusFilter } : {}),
      whsService.listInspectionSchedules(),
    ]);
    if (insRes.success) setInspections(insRes.data);
    else setError(insRes.error.message);
    if (schRes.success) setSchedules(schRes.data);
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const loadOptions = useCallback(async () => {
    const [tplRes, optRes, userRes] = await Promise.all([
      whsService.listChecklistTemplates('inspection'),
      whsService.getReportOptions(),
      userService.getUsers(),
    ]);
    if (tplRes.success) setTemplates(tplRes.data);
    if (optRes.success) setFarms(optRes.data.farms);
    if (userRes.success) setUsers(userRes.data);
  }, []);

  const openModal = () => {
    setFormError(null);
    setTitle(''); setTemplateId(''); setArea(''); setFarmId(''); setScheduledDate(''); setInspectorId('');
    setShowModal(true);
    loadOptions();
  };

  const handleCreate = async () => {
    if (!title.trim()) { setFormError('Title is required.'); return; }
    setSaving(true);
    setFormError(null);
    const payload: Record<string, unknown> = { title: title.trim() };
    if (templateId) payload.template_id = Number(templateId);
    if (area.trim()) payload.area = area.trim();
    if (farmId) payload.farm_id = Number(farmId);
    if (scheduledDate) payload.scheduled_date = scheduledDate;
    if (inspectorId) payload.inspector_id = Number(inspectorId);
    const res = await whsService.createInspection(payload);
    setSaving(false);
    if (res.success) {
      setShowModal(false);
      navigate(`/hr/whs/inspections/${res.data.id}`);
    } else {
      setFormError(res.error.message);
    }
  };

  const openScheduleModal = (existing?: InspectionSchedule) => {
    setScheduleError(null);
    if (existing) {
      setEditingScheduleId(existing.id);
      setScheduleForm({
        title: existing.title,
        frequency: existing.frequency,
        interval_days: existing.interval_days != null ? String(existing.interval_days) : '',
        start_date: existing.next_due_date || '',
        template_id: existing.template_id != null ? String(existing.template_id) : '',
        area: existing.area || '',
        farm_id: existing.farm_id != null ? String(existing.farm_id) : '',
        inspector_id: existing.inspector_id != null ? String(existing.inspector_id) : '',
        notes: existing.notes || '',
      });
    } else {
      setEditingScheduleId(null);
      setScheduleForm(EMPTY_SCHEDULE);
    }
    setShowSchedule(true);
    loadOptions();
  };

  const setSF = <K extends keyof ScheduleForm>(key: K, value: ScheduleForm[K]) =>
    setScheduleForm((prev) => ({ ...prev, [key]: value }));

  const handleSaveSchedule = async () => {
    if (!scheduleForm.title.trim()) { setScheduleError('Title is required.'); return; }
    if (!editingScheduleId && !scheduleForm.start_date) {
      setScheduleError('A start date is required so the first inspection can be generated.');
      return;
    }
    setScheduleSaving(true);
    setScheduleError(null);
    const payload: Record<string, unknown> = {
      title: scheduleForm.title.trim(),
      frequency: scheduleForm.frequency,
    };
    payload.area = scheduleForm.area.trim() || null;
    payload.template_id = scheduleForm.template_id ? Number(scheduleForm.template_id) : null;
    payload.farm_id = scheduleForm.farm_id ? Number(scheduleForm.farm_id) : null;
    payload.inspector_id = scheduleForm.inspector_id ? Number(scheduleForm.inspector_id) : null;
    payload.notes = scheduleForm.notes.trim() || null;
    if (scheduleForm.frequency === 'custom') {
      payload.interval_days = scheduleForm.interval_days ? Number(scheduleForm.interval_days) : null;
    }
    if (scheduleForm.start_date) {
      // For a new schedule this is the first due date; for edits it moves the next due date.
      if (editingScheduleId) payload.next_due_date = scheduleForm.start_date;
      else payload.start_date = scheduleForm.start_date;
    }

    const res = editingScheduleId
      ? await whsService.updateInspectionSchedule(editingScheduleId, payload)
      : await whsService.createInspectionSchedule(payload);
    setScheduleSaving(false);
    if (res.success) {
      setShowSchedule(false);
      load();
    } else {
      setScheduleError(res.error.message);
    }
  };

  const toggleScheduleActive = async (s: InspectionSchedule) => {
    const res = await whsService.updateInspectionSchedule(s.id, { is_active: !s.is_active });
    if (res.success) load();
  };

  const deleteSchedule = async (s: InspectionSchedule) => {
    if (!window.confirm(`Delete the schedule "${s.title}"? Existing inspections it created are kept.`)) return;
    const res = await whsService.deleteInspectionSchedule(s.id);
    if (res.success) load();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Inspections"
        subtitle="Scheduled workplace & plant inspections from checklist templates"
        icon={<ClipboardList className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        {/* Recurring schedules */}
        <div className="mb-8 rounded-xl border border-neutral-200 bg-white">
          <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
            <div className="flex items-center gap-2">
              <Repeat className="h-5 w-5 text-primary-600" />
              <h2 className="text-base font-semibold text-neutral-900">Recurring schedules</h2>
            </div>
            <Button onClick={() => openScheduleModal()} size="sm" variant="secondary">
              <Plus className="h-4 w-4" /> New schedule
            </Button>
          </div>
          {schedules.length === 0 ? (
            <div className="px-5 py-6 text-sm text-neutral-500">
              No recurring schedules yet. Create one and its first inspection is generated automatically; each time an
              inspection is completed, the next is scheduled on the chosen cadence.
            </div>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {schedules.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-neutral-900">{s.title}</span>
                      <Badge label={FREQUENCY_LABEL[s.frequency]} className="bg-primary-50 text-primary-700" />
                      {!s.is_active && <Badge label="Paused" className="bg-neutral-200 text-neutral-600" />}
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-x-4 text-xs text-neutral-500">
                      {s.area && <span>{s.area}</span>}
                      {s.farm_name && <span>{s.farm_name}</span>}
                      <span>Inspector: {inspectorName(s)}</span>
                      {s.template_name && <span>Checklist: {s.template_name}</span>}
                      <span className="inline-flex items-center gap-1">
                        <CalendarClock className="h-3.5 w-3.5" />
                        Next: {s.next_due_date ? formatLocalDateInTz(s.next_due_date, orgTz) : '—'}
                      </span>
                      {typeof s.generated_count === 'number' && <span>{s.generated_count} generated</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleScheduleActive(s)}
                      title={s.is_active ? 'Pause' : 'Resume'}
                      className="rounded p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
                    >
                      {s.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => openScheduleModal(s)}
                      title="Edit"
                      className="rounded p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => deleteSchedule(s)}
                      title="Delete"
                      className="rounded p-1.5 text-neutral-500 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value || 'all'}
                onClick={() => setStatusFilter(f.value)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  statusFilter === f.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <Button onClick={openModal} size="sm">
            <Plus className="h-4 w-4" /> New inspection
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-neutral-500">Loading…</p>
        ) : inspections.length === 0 ? (
          <div className="rounded-xl border border-neutral-200 bg-white p-10 text-center">
            <ClipboardList className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
            <p className="text-sm text-neutral-500">No inspections found.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Area / Farm</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Inspector</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wide">Score</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wide">Items</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {inspections.map((i) => (
                  <tr
                    key={i.id}
                    onClick={() => navigate(`/hr/whs/inspections/${i.id}`)}
                    className="cursor-pointer hover:bg-neutral-50"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-neutral-900">
                      <span className="inline-flex items-center gap-1.5">
                        {i.title}
                        {i.schedule_id ? <Repeat className="h-3.5 w-3.5 text-primary-500" aria-label="Recurring" /> : null}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-600">
                      {i.area || '—'}
                      {i.farm_name ? <span className="block text-xs text-neutral-400">{i.farm_name}</span> : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-600">{inspectorName(i)}</td>
                    <td className="px-4 py-3 text-sm text-neutral-600">
                      {i.completed_date
                        ? formatLocalDateInTz(i.completed_date, orgTz)
                        : i.scheduled_date
                        ? formatLocalDateInTz(i.scheduled_date, orgTz)
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <Badge label={INSPECTION_STATUS_LABEL[i.status]} className={INSPECTION_STATUS_CLASS[i.status]} />
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-neutral-700">
                      {i.score != null ? `${i.score}%` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-neutral-700">
                      {i.item_count}
                      {i.fail_count > 0 ? <span className="ml-1 font-semibold text-red-600">({i.fail_count} fail)</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* One-off inspection modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-neutral-900">New inspection</h2>
              <button onClick={() => setShowModal(false)} className="text-neutral-400 hover:text-neutral-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              {formError && (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {formError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Title <span className="text-red-500">*</span></label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="e.g. Monthly workshop inspection"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Checklist template</label>
                <select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">No template (blank)</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.is_system_template ? ' (template)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Area</label>
                <input
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="e.g. Packing shed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Farm</label>
                <select
                  value={farmId}
                  onChange={(e) => setFarmId(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">No farm</option>
                  {farms.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Scheduled date</label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Inspector</label>
                <select
                  value={inspectorId}
                  onChange={(e) => setInspectorId(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">Unassigned</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{userName(u)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-4">
              <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCreate} isLoading={saving}>Create</Button>
            </div>
          </div>
        </div>
      )}

      {/* Recurring schedule modal */}
      {showSchedule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-neutral-900">
                {editingScheduleId ? 'Edit schedule' : 'New inspection schedule'}
              </h2>
              <button onClick={() => setShowSchedule(false)} className="text-neutral-400 hover:text-neutral-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              {scheduleError && (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {scheduleError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Title <span className="text-red-500">*</span></label>
                <input
                  value={scheduleForm.title}
                  onChange={(e) => setSF('title', e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="e.g. Monthly workshop inspection"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Frequency</label>
                  <select
                    value={scheduleForm.frequency}
                    onChange={(e) => setSF('frequency', e.target.value as ScheduleFrequency)}
                    className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    {(Object.keys(FREQUENCY_LABEL) as ScheduleFrequency[]).map((f) => (
                      <option key={f} value={f}>{FREQUENCY_LABEL[f]}</option>
                    ))}
                  </select>
                </div>
                {scheduleForm.frequency === 'custom' && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Every (days)</label>
                    <input
                      type="number"
                      min={1}
                      value={scheduleForm.interval_days}
                      onChange={(e) => setSF('interval_days', e.target.value)}
                      className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {editingScheduleId ? 'Next due date' : 'First inspection date'}
                  {!editingScheduleId && <span className="text-red-500"> *</span>}
                </label>
                <input
                  type="date"
                  value={scheduleForm.start_date}
                  onChange={(e) => setSF('start_date', e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Checklist template</label>
                <select
                  value={scheduleForm.template_id}
                  onChange={(e) => setSF('template_id', e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">No template (blank)</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.is_system_template ? ' (template)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Area</label>
                  <input
                    value={scheduleForm.area}
                    onChange={(e) => setSF('area', e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="e.g. Packing shed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Farm</label>
                  <select
                    value={scheduleForm.farm_id}
                    onChange={(e) => setSF('farm_id', e.target.value)}
                    className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="">No farm</option>
                    {farms.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Inspector</label>
                <select
                  value={scheduleForm.inspector_id}
                  onChange={(e) => setSF('inspector_id', e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">Unassigned</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{userName(u)}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-neutral-400">The inspector is notified each time a new inspection is generated.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
                <textarea
                  value={scheduleForm.notes}
                  onChange={(e) => setSF('notes', e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-4">
              <Button variant="secondary" size="sm" onClick={() => setShowSchedule(false)}>Cancel</Button>
              <Button size="sm" onClick={handleSaveSchedule} isLoading={scheduleSaving}>
                {editingScheduleId ? 'Save' : 'Create schedule'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WhsInspections() {
  return <WhsGate><WhsInspectionsInner /></WhsGate>;
}
